import React from 'react';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { TabPane } from './TabPane';
import { Field } from 'redux-form';
import { MultiselectField, NumberField, SelectRow, ToggleSwitchRow } from '.';
import states from '../../utils/states';
import _ from 'lodash';
import {
  useAibList,
  useBrowsers,
  useDevices,
  useDomains,
  useOperatingSystems,
  useAffiliates,
} from '../../views/OfferBlocks/queries';
import { renderCriteriaOptions, renderMultiSelect, textarea } from '../FormFields/reduxFormRenderers';
import { normalizeZips } from '../../utils/conversionFunctions';
import PreviousRegistration from './PreviousRegistration';
import { useUserSelectors } from './hooks';
import { useAffiliateTags } from '../../views/Affiliates/queries';

const CRITERIA_TYPE = 'Offer Action Criteria';

export const UsersPane = (props) => {
  const { data: aibLists } = useAibList(true, (data) =>
    data.map((aibList) => aibList.listCode).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: browsers } = useBrowsers(true, (data) =>
    data.map((browser) => browser.name).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: domains, isLoading: domainsLoading } = useDomains(
    true,
    (data) =>
      [
        { id: null, name: 'All Domains' },
        { id: null, name: 'Current Domain' },
        ...data.map((domain) => ({
          id: domain.id,
          name: domain.name,
        })),
      ].sort((a, b) => (a.name > b.name ? 1 : -1)) // Sort by name
  );
  const { data: domainTags, isLoading: domainTagsLoading } = useAffiliateTags('domain');
  const domainsWithTags =
    domainsLoading || domainTagsLoading
      ? []
      : [...(domainTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []), ...domains];

  const { data: affiliates, isLoading: affiliatesLoading } = useAffiliates(true, (data) =>
    _.orderBy(data, ['name'], ['asc']).map(({ name, networkAffiliateId }) => ({
      id: networkAffiliateId,
      name,
    }))
  );
  const { data: affiliateTags, isLoading: affiliateTagsLoading } = useAffiliateTags('affiliate');
  const affiliatesWithTags =
    affiliatesLoading || affiliateTagsLoading
      ? []
      : [...(affiliateTags?.map((tag) => ({ id: `#${tag.name}`, name: `#${tag.name}` })) || []), ...(affiliates || [])];

  const { data: devices } = useDevices(true, (data) =>
    data.map((device) => device.name).sort((a, b) => (a > b ? 1 : -1))
  );
  const { data: operatingSystems } = useOperatingSystems(true, (data) =>
    data.map((operatingSystem) => operatingSystem.name).sort((a, b) => (a > b ? 1 : -1))
  );

  let domainsForPreviousRegReq = domainsWithTags ? [...domainsWithTags] : [];
  let affiliatesForPreviousRegReq = affiliatesWithTags ? [...affiliatesWithTags] : [];

  const mobileBrowsers = ['Chrome', 'Opera', 'Firefox', 'Safari', 'IE', 'Other'];
  const {
    browserType,
    criteriaAnuraScore,
    criteriaIncludeOrExcludeStates,
    criteriaIncludeOrExcludeZips,
    criteriaIncludeOrExcludeOperatingSystems,
    criteriaIncludeOrExcludeBrowsers,
    criteriaIncludeOrExcludeDomains,
    criteriaIncludeOrExcludeDevices,
    userIdLastDigitIncludeExclude,
    criteriaAibListIncludeExclude,
    criteriaUserDataIncludeExclude,
    criteriaZips,
    jobKeywordIncludeExclude,
    ipqsConfiguration,
  } = useUserSelectors();

  return (
    <TabPane tabId='Users'>
      <ToggleSwitchRow label='TCPA Opt-in Required' name='criteriaOfferRequiresOptIn' />
      <ToggleSwitchRow label='TCPA Opt-in Required Over Last Interval' name='criteriaTcpaLastOptInInterval' />
      <ToggleSwitchRow label='Brite Verify Phone Validated' name='criteriaPhoneHasBeenVerifiedWithBriteverify' />
      <ToggleSwitchRow label='Brite Verify Email Validated' name='criteriaEmailHasBeenVerifiedWithBriteverify' />
      <ToggleSwitchRow label='Include users in the phone BlackList' name='criteriaPhoneBlacklist' />
      <ToggleSwitchRow label='English Browser Language Required' name='englishBrowserLanguageRequired' />
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Field name='criteriaMinAge' component={NumberField} label='Min Age' />
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Field name='criteriaMaxAge' component={NumberField} label='Max Age' />
        </Grid>
      </Grid>
      <SelectRow
        name='criteriaExcludeBrowserType'
        label='Exclude Browser Type'
        options={[
          { value: '', option: 'Select' },
          { value: 'Desktop', option: 'Desktop' },
          { value: 'Mobile', option: 'Mobile' },
        ]}
      />
      <SelectRow
        name='criteriaExcludeGender'
        label='Exclude Gender'
        options={[
          { value: '', option: 'Select' },
          { value: 'female', option: 'Female' },
          { value: 'male', option: 'Male' },
        ]}
      />
      <SelectRow
        name='criteriaAnuraScore'
        label='Anura Score'
        options={[
          { value: '', option: 'Select' },
          { value: 'NONSUSPECT', option: 'Non-suspect' },
          { value: 'SUSPECT', option: 'Suspect' },
          { value: 'NOSCORE', option: 'No Score' },
        ]}
      />
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Typography variant='h5'>Required User Data</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Field name='criteriaUserDataIncludeExclude' component={renderCriteriaOptions} type='select' />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          {criteriaUserDataIncludeExclude === 'Include' || criteriaUserDataIncludeExclude === 'Exclude' ? (
            <Field
              name='criteriaRequiredUserData'
              component={MultiselectField}
              data={[
                'email',
                'first_name',
                'last_name',
                'gender',
                'dob',
                'phone',
                'zip',
                'state',
                'city',
                'address_1',
                'ip_zip',
              ]}
            />
          ) : null}
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Typography variant='h5'>AIB List</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Field name='criteriaAibListIncludeExclude' component={renderCriteriaOptions} type='select' />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          {(criteriaAibListIncludeExclude === 'Include' || criteriaAibListIncludeExclude === 'Exclude') && (
            <Field name='criteriaAibList' component={renderMultiSelect} data={aibLists} />
          )}
        </Grid>
      </Grid>
      <Grid container spacing={2} sx={{ mt: 1, alignItems: 'start' }}>
        <Grid size={{ xs: 12, lg: 2 }}>
          <Typography variant='h5'>Previous Registration Required</Typography>
        </Grid>
        <Grid size={{ xs: 12, lg: 10 }}>
          <PreviousRegistration
            domainsForPreviousRegReq={domainsForPreviousRegReq}
            affiliatesForPreviousRegReq={affiliatesForPreviousRegReq}
            isLoading={domainsLoading || domainTagsLoading || affiliatesLoading || affiliateTagsLoading}
          />
        </Grid>
      </Grid>
      {(props.criteriaType === 'Offer Criteria' || props.criteriaType === 'Offer Action Criteria') && (
        <SelectRow
          label='IPQS Configuration'
          name='ipqsConfiguration.shouldProcess'
          options={[
            { value: 'Enabled', option: 'Enabled' },
            { value: 'Disabled', option: 'Disabled' },
          ]}
        >
          {ipqsConfiguration?.shouldProcess === 'Enabled' && (
            <>
              <Grid size={{ xs: 12, lg: 2 }}>
                <Field name='ipqsConfiguration.email' component={NumberField} label='Email' />
              </Grid>
              <Grid size={{ xs: 12, lg: 2 }}>
                <Field name='ipqsConfiguration.ip' component={NumberField} label='IP' />
              </Grid>
              <Grid size={{ xs: 12, lg: 2 }}>
                <Field name='ipqsConfiguration.phone' component={NumberField} label='Phone' />
              </Grid>
              <Grid size={{ xs: 12, lg: 2 }}>
                <Field name='ipqsConfiguration.fingerprint' component={NumberField} label='Fingerprint' />
              </Grid>
            </>
          )}
        </SelectRow>
      )}
      <SelectRow name='criteriaNullSourceNewUserIncludeExclude' label='Null Source New Users' />
      <SelectRow label='User_ID ends with' name='criteriaIncludeOrExcludeUserIdLastDigit'>
        {(userIdLastDigitIncludeExclude === 'Include' || userIdLastDigitIncludeExclude === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field
              name='criteriaUserIdLastDigitString'
              component={renderMultiSelect}
              data={[0, 1, 2, 3, 4, 5, 6, 7, 8, 9]}
              allowCreate={true}
            />
          </Grid>
        )}
      </SelectRow>
      <SelectRow label='Device' name='criteriaIncludeOrExcludeDevices'>
        {(criteriaIncludeOrExcludeDevices === 'Include' || criteriaIncludeOrExcludeDevices === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field name='criteriaDevices' component={renderMultiSelect} data={devices} />
          </Grid>
        )}
      </SelectRow>
      <SelectRow label='Browsers' name='criteriaIncludeOrExcludeBrowsers'>
        {(criteriaIncludeOrExcludeBrowsers === 'Include' || criteriaIncludeOrExcludeBrowsers === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field
              name='criteriaBrowsers'
              component={renderMultiSelect}
              data={browserType === 'Only mobile' ? mobileBrowsers : browsers}
            />
          </Grid>
        )}
      </SelectRow>
      <SelectRow label='What If Domain' name='criteriaIncludeOrExcludeDomains'>
        {(criteriaIncludeOrExcludeDomains === 'Include' || criteriaIncludeOrExcludeDomains === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field
              className='text-white'
              name='criteriaDomains'
              id='whatif-domains'
              component={renderMultiSelect}
              textField={(item) => (item.name && item.name.startsWith('#') ? `${item.id}` : `${item.id}: ${item.name}`)}
              dataKey='name'
              data={domainsWithTags}
              busy={domainsLoading || domainTagsLoading}
              groupBy={(item) => (item.name.startsWith('#') ? 0 : 1)}
              renderListGroup={({ group }) => (group === 0 ? <span>Tags</span> : <span>Domains</span>)}
            />
          </Grid>
        )}
      </SelectRow>
      <SelectRow label='Operating System' name='criteriaIncludeOrExcludeOperatingSystems'>
        {(criteriaIncludeOrExcludeOperatingSystems === 'Include' ||
          criteriaIncludeOrExcludeOperatingSystems === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field name='criteriaOperatingSystems' component={renderMultiSelect} data={operatingSystems} />
          </Grid>
        )}
      </SelectRow>
      <SelectRow label='States' name='criteriaIncludeOrExcludeStates'>
        {(criteriaIncludeOrExcludeStates === 'Include' || criteriaIncludeOrExcludeStates === 'Exclude') && (
          <Grid size={{ xs: 12, lg: 4 }}>
            <Field name='criteriaStates' component={renderMultiSelect} data={states} />
          </Grid>
        )}
      </SelectRow>
      <SelectRow label='Zip Code' name='criteriaIncludeOrExcludeZips'>
        {(criteriaIncludeOrExcludeZips === 'Include' || criteriaIncludeOrExcludeZips === 'Exclude') && (
          <>
            <Grid size={{ xs: 12, lg: 4 }}>
              <Field name='criteriaZips' component={textarea} normalize={normalizeZips} />
            </Grid>
            <Grid size={{ xs: 12, lg: 2 }}>
              Count: {criteriaZips?.split(/\s+/).filter((zip) => zip.trim().length === 5 && !isNaN(zip)).length}
              <br />
              Unique: {new Set(criteriaZips?.split(/\s+/).filter((zip) => zip.trim().length === 5 && !isNaN(zip))).size}
            </Grid>
          </>
        )}
      </SelectRow>
      {props.criteriaType === CRITERIA_TYPE && (
        <SelectRow
          label='Job Keyword'
          name='jobKeyword.includeExclude'
          options={[
            { value: 'None', option: 'None' },
            { value: 'Include', option: 'Include' },
            { value: 'Exclude', option: 'Exclude' },
          ]}
        >
          {(jobKeywordIncludeExclude === 'Include' || jobKeywordIncludeExclude === 'Exclude') && (
            <>
              <Grid size={{ xs: 12, lg: 4 }}>
                <Field name='jobKeyword.jobKeywords' component={textarea} />
              </Grid>
            </>
          )}
        </SelectRow>
      )}
    </TabPane>
  );
};
